/* eslint-disable max-lines */
/* eslint-disable camelcase */
import { Tag, TagGroup } from '@litlingo/client';
import { ActionCreatorWithOptionalPayload } from '@reduxjs/toolkit';
import { fetchAllTagGroups, fetchFiltersCounts } from 'actions';
import ShowLabels from 'components/Filters/BackendResourceFilter/ShowLabels';
import LoadingIndicator from 'components/LoadingIndicator';
import useClickOutside from 'components/utils/useClickOutside';
import { EXTERNAL_LINK_WHITE_ICON } from 'constants/commonIcons';
import { SEARCH_ICON } from 'constants/filterIcons';
import { FiltersTreeContext } from 'contexts/ExploreFiltersTreeContext';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import LoadingOverlayWrapper from 'react-loading-overlay-ts';
import { useDispatch } from 'react-redux';
import { getFetchAllTagGroupsLoading, getTagGroupsWithCounts } from 'selectors/tagGroup';
import { getFetchAllTagsLoading, getTagsWithCounts } from 'selectors/tags';
import { useSelector } from 'store';
import { Selector } from 'types';
import logEvent from 'utils/analytics';
import { triggerURIDownload } from 'utils/triggerDownload';
import TagFilterItem from './TagFilterItem';
import TagGroupsFilterItem from './TagGroupsFilterItem';

type ResourceFilterProps = {
  className?: string;
  title?: string;
  dataTestid?: string;
  isOpening?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fetchActionTag: ActionCreatorWithOptionalPayload<any, string>;
  getTagCount: Selector<number>;

  treeContext: React.Context<FiltersTreeContext>;
};

const TagsFilter: React.FC<ResourceFilterProps> = (props) => {
  const {
    className,
    title = '',
    dataTestid,
    isOpening,
    fetchActionTag,
    getTagCount,
    treeContext,
  } = props;

  const tagGroupsKey = 'tag_value_groups';
  const tagsKey = 'tags';

  const dispatch = useDispatch();

  const {
    tree,
    treeParams,
    filtersSearchString: filterSearchString,
    handleAddValueToTree,
    handleRemoveValueFromTree,
  } = useContext(treeContext);

  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const [offset, setOffset] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);

  const allTagGroups = useSelector(getTagGroupsWithCounts);
  const allTags = useSelector(getTagsWithCounts);

  const totalCountTags = useSelector(getTagCount);

  const loadingTagGroups = useSelector(getFetchAllTagGroupsLoading);
  const loadingTags = useSelector(getFetchAllTagsLoading);
  const loading = loadingTagGroups || loadingTags;

  const filterTagGroupsItems = (treeParams[tagGroupsKey] ?? []) as string[];
  const notFilterTagGroupsItems = (treeParams[`not_${tagGroupsKey}`] ?? []) as string[];

  const filterTagsItems = (treeParams[tagsKey] ?? []) as string[];
  const notFilterTagsItems = (treeParams[`not_${tagsKey}`] ?? []) as string[];

  const handleClickOutside = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);
  const wrapperRef = useRef(null);
  useClickOutside(wrapperRef, handleClickOutside);

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchFiltersCounts({ fields: ['tags', 'tag_groups'], filterSearchString }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, filterSearchString, isOpen]);

  useEffect(() => {
    if (isOpen) {
      /* setFirstLoad(false); */
      dispatch(
        fetchAllTagGroups({
          limit: -1,
          broad_search: debouncedSearchValue,
        })
      );
      dispatch(
        fetchActionTag({
          searchValue: debouncedSearchValue,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isOpen, debouncedSearchValue, fetchActionTag]);

  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedSearchValue(searchValue), 300);
    return () => clearTimeout(timeoutId);
  }, [searchValue]);

  useEffect(() => {
    if (isOpening) {
      inputRef.current?.focus();
    }
  }, [inputRef, isOpening]);

  const handleButtonClick = (): void => {
    if (isOpen) return;
    setIsOpen(!isOpen);
  };

  const handleDelete = (labelId: string, exclude: boolean): void => {
    if (filterTagsItems.includes(labelId) || notFilterTagsItems.includes(labelId)) {
      if (exclude) {
        handleRemoveValueFromTree(labelId, `not_${tagsKey}`.replace('_and', ''));
      } else {
        handleRemoveValueFromTree(labelId, tagsKey.replace('_and', ''));
      }
    } else if (exclude) {
      handleRemoveValueFromTree(labelId, `not_${tagGroupsKey}`.replace('_and', ''));
    } else {
      handleRemoveValueFromTree(labelId, tagGroupsKey.replace('_and', ''));
    }
  };

  const handleInputChange = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    exclude: boolean,
    state: string,
    checked: string | undefined,
    isTag: boolean
  ): void => {
    logEvent('envelopes-list-filter-models');

    if (
      exclude &&
      ((isTag && filterTagsItems.includes(state)) ||
        (!isTag && filterTagGroupsItems.includes(state)))
    ) {
      return;
    }
    if (
      !exclude &&
      ((isTag && notFilterTagsItems.includes(state)) ||
        (!isTag && notFilterTagGroupsItems.includes(state)))
    ) {
      return;
    }

    let tagsNames = [];
    if (isTag) {
      if (exclude) {
        tagsNames = [...notFilterTagsItems];
      } else {
        tagsNames = [...filterTagsItems];
      }
    } else if (exclude) {
      tagsNames = [...notFilterTagGroupsItems];
    } else {
      tagsNames = [...filterTagGroupsItems];
    }

    const item =
      allTags.find((t) => t.uuid === state) || allTagGroups.find((g) => g.uuid === state);

    if (checked === 'true') {
      let key = '';
      tagsNames.push(state);

      if (isTag) {
        key = tagsKey;
      } else {
        key = tagGroupsKey;
      }

      const label = item && 'value' in item ? item.value : item?.name;

      if (exclude) {
        handleAddValueToTree(state, `not_${key}`.replace('and_', ''), label || '');
      } else {
        handleAddValueToTree(state, key.replace('_and', ''), label || '');
      }
    } else {
      tagsNames = tagsNames.filter((s) => s !== state);
      handleDelete(state, exclude);
    }

    event.stopPropagation();
  };

  const handleLoadMore = (): void => {
    if (isOpen) {
      dispatch(
        fetchActionTag({
          limit: 25,
          include_envelopes_count: false,
          broad_search: debouncedSearchValue,
          offset: offset + 25,
        })
      );
      setOffset((o) => o + 25);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e?.target.value.toLowerCase();
    setSearchValue(value);
  };

  const handleExportCounts = (): void => {
    const tagGroupsData = allTagGroups.map((t) => ({
      name: t.name,
      count: !t.count ? 0 : t.count,
    }));
    const tagsData = allTags.map((t) => ({
      name: t.value,
      count: !t.count ? 0 : t.count,
    }));
    const data = [...tagGroupsData, ...tagsData];

    const stringData = data.reduce((acc, val) => `${acc}${val.name},${val.count}\n`, '');
    const formattedData = `data:text/csv;charset=utf-8,Name,Count\n${stringData}`;

    triggerURIDownload(formattedData, title);
  };

  return (
    <>
      <div ref={wrapperRef} className={`flex flex-col ${className || ''}"`}>
        <div className="relative flex flex-row justify-start items-center w-full min-h-8 rounded p-1.5 bg-litlingo-success-light bg-opacity-10">
          <div className="w-5">{SEARCH_ICON('white')}</div>
          <input
            ref={inputRef}
            id="search"
            name="search"
            className="max-h-5 w-full text-white ml-1 font-normal text-base bg-transparent placeholder-white placeholder-italic"
            placeholder={`Search ${title}`}
            data-testid={dataTestid}
            type="text"
            onFocus={handleButtonClick}
            onChange={handleSearchChange}
            value={searchValue}
            autoComplete="off"
          />
        </div>

        {isOpen && (
          <>
            <LoadingOverlayWrapper active={loading} spinner={<LoadingIndicator />} fadeSpeed={100}>
              <div className="w-full max-h-filter z-10 overflow-auto bg-litlingo-gray-1 custom-scrollbar">
                <fieldset className="min-h-5 flex flex-col">
                  {[...allTagGroups].map((item: TagGroup) => (
                    <TagGroupsFilterItem
                      key={item.uuid}
                      tagGroup={item}
                      tags={item.tag_values}
                      handleInputChange={handleInputChange}
                      treeContext={treeContext}
                      searchValue={debouncedSearchValue}
                    />
                  ))}
                  {[...allTags]
                    .filter((t) => t.tag_value_group_uuid === null)
                    .map((item: Tag) => (
                      <TagFilterItem
                        key={item.uuid}
                        tag={item}
                        handleInputChange={handleInputChange}
                        treeContext={treeContext}
                      />
                    ))}
                </fieldset>
                {offset + 25 < totalCountTags - 1 && (
                  <button
                    type="button"
                    className="w-full py-1 text-small border-t border-litlingo-gray-2 focus:outline-none"
                    onClick={handleLoadMore}
                  >
                    <span className="text-litlingo-primary">Load More</span>
                  </button>
                )}
              </div>
            </LoadingOverlayWrapper>

            <div className="mt-2 -mb-2 flex flex-row justify-between items-center">
              <button type="button" className="focus:outline-none" onClick={handleExportCounts}>
                {EXTERNAL_LINK_WHITE_ICON}
              </button>
              <span className="text-white font-normal text-xss">Shift + click to exclude</span>
            </div>
          </>
        )}

        {!isOpen && (
          <>
            <ShowLabels
              handleDelete={handleDelete}
              filterData={filterTagGroupsItems}
              notFilterData={notFilterTagGroupsItems}
              getResourceKey={tagGroupsKey}
              tree={tree}
            />
            <ShowLabels
              handleDelete={handleDelete}
              filterData={filterTagsItems}
              notFilterData={notFilterTagsItems}
              getResourceKey={tagsKey}
              tree={tree}
            />
          </>
        )}
      </div>
    </>
  );
};

export default TagsFilter;
